import React, { useEffect, useState } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import { Box, Paper, Typography, Stack, TextField, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../../services/api';
import { EXAM_MODES } from '../../config/Config';

const ExamModeManagementPage = () => {
  const [modes, setModes] = useState([]);
  const [newMode, setNewMode] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [openAdd, setOpenAdd] = useState(false);

  const fetchModes = async () => {
    try {
      setLoading(true);
      const res = await api.get(EXAM_MODES);
      setModes(res.data.results || res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchModes(); }, []);

  const handleAdd = async () => {
    const mode = newMode.trim();
    if (!mode) return;
    try {
      setSaving(true);
      await api.post(EXAM_MODES, { mode });
      setNewMode('');
      setMessageType('success');
      setMessage('Mode added');
      setOpenAdd(false);
      fetchModes();
    } catch (e) {
      setMessageType('error');
      const detail = e?.response?.data?.detail || e?.response?.data?.mode || 'Failed to add mode';
      setMessage(Array.isArray(detail) ? detail.join(', ') : String(detail));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, mode) => {
    try {
      await api.patch(`${EXAM_MODES}${id}/`, { mode });
      setMessageType('success');
      setMessage('Mode updated');
      fetchModes();
    } catch (e) {
      setMessageType('error');
      const detail = e?.response?.data?.detail || e?.response?.data?.mode || 'Failed to update mode';
      setMessage(Array.isArray(detail) ? detail.join(', ') : String(detail));
      throw e;
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'mode', headerName: 'Mode', flex: 1, editable: true },
  ];

  return (
    <AdminLayoutWrapper>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Exam Modes</Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={()=>{ setNewMode(''); setOpenAdd(true); }}>Add</Button>
          </Stack>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <div style={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={modes}
              columns={columns}
              loading={loading}
              getRowId={(r)=>r.id}
              processRowUpdate={async (row) => { await handleUpdate(row.id, row.mode); return row; }}
              onProcessRowUpdateError={(err)=>{
                setMessageType('error');
                setMessage('Failed to update mode');
              }}
              experimentalFeatures={{ newEditingApi: true }}
            />
          </div>
        </Paper>
        <Dialog open={openAdd} onClose={()=>setOpenAdd(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add Exam Mode</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Mode name"
              fullWidth
              value={newMode}
              onChange={(e)=>setNewMode(e.target.value)}
              onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); handleAdd(); } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setOpenAdd(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAdd} disabled={!newMode.trim() || saving}>Add</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={!!message} autoHideDuration={2500} onClose={()=>setMessage('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={messageType} onClose={()=>setMessage('')} sx={{ width: '100%' }}>{message}</Alert>
        </Snackbar>
      </Box>
    </AdminLayoutWrapper>
  );
};

export default ExamModeManagementPage;


